import type { FC, ReactNode } from 'react'
import type { AxisFormat } from '@/components/charts/chart-utils'

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { ChartFrame } from '@/components/charts/chart-frame'
import { ChartTooltip, ChartTooltipRow, Swatch } from '@/components/charts/chart-tooltip'
import { niceScale, useActiveIndex } from '@/components/charts/chart-utils'

export interface ChartSeries {
    key: string
    label: string
    color: string
}

export interface StackedBarDatum {
    key: string
    label: string
    tooltipLabel: string
    values: Record<string, number>
}

interface StackedBarChartProps {
    data: StackedBarDatum[]
    // Listed bottom to top, in the fixed categorical order
    series: ChartSeries[]
    formatValue: (value: number) => string
    ariaLabel: string
    axisFormat?: AxisFormat
    height?: number
    totalLabel?: string
}

// Stacked columns anchored to the zero baseline, a 2px surface gap between segments
// and a 4px rounded top on the data end.
export const StackedBarChart: FC<StackedBarChartProps> = ({
    data,
    series,
    formatValue,
    ariaLabel,
    axisFormat = 'compact',
    height = 280,
    totalLabel = 'Total'
}): ReactNode => {
    const count = data.length
    const { active, setActive, onKeyDown, onFocus, onBlur } = useActiveIndex(count)

    const totals = useMemo(() => data.map((datum) => series.reduce((sum, item) => sum + (datum.values[item.key] ?? 0), 0)), [data, series])
    const scale = useMemo(() => niceScale(0, Math.max(...totals, 0), 4, true), [totals])
    const center = (index: number): number => ((index + 0.5) / count) * 100
    const activeDatum = active === null ? null : data[active]

    return (
        <div className='w-full'>
            <ChartFrame
                height={height}
                scale={scale}
                axisFormat={axisFormat}
                xAxis={(
                    <div className='flex h-full'>
                        {data.map((datum, index) => (
                            <span
                                key={datum.key}
                                className={cn(
                                    'min-w-0 flex-1 pt-2 text-center text-[11px] leading-none whitespace-nowrap text-muted-foreground',
                                    count > 6 && index % 2 === 1 && 'max-sm:invisible'
                                )}
                            >
                                {datum.label}
                            </span>
                        ))}
                    </div>
                )}
            >
                <div
                    role='group'
                    aria-roledescription='grafik batang bertumpuk'
                    aria-label={`${ariaLabel}. Gunakan tombol panah untuk membaca setiap kolom.`}
                    tabIndex={0}
                    className='absolute inset-0 flex rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/50'
                    onKeyDown={onKeyDown}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onPointerLeave={(event) => {
                        if (event.pointerType === 'mouse') setActive(null)
                    }}
                >
                    {data.map((datum, index) => {
                        const total = totals[index]
                        const visible = series.filter((item) => (datum.values[item.key] ?? 0) > 0)

                        return (
                            <div
                                key={datum.key}
                                className='flex h-full min-w-0 flex-1 items-end justify-center'
                                onPointerEnter={() => setActive(index)}
                                onPointerDown={() => setActive(index)}
                            >
                                <div
                                    className={cn(
                                        'flex w-[58%] max-w-12 flex-col-reverse transition-opacity duration-150',
                                        active !== null && active !== index && 'opacity-40'
                                    )}
                                    style={{ height: `${scale.max === 0 ? 0 : (total / scale.max) * 100}%` }}
                                >
                                    {visible.map((item, position) => (
                                        <div
                                            key={item.key}
                                            className={cn(
                                                'w-full shrink-0',
                                                position > 0 && 'border-b-2 border-card',
                                                position === visible.length - 1 && 'rounded-t-[4px]'
                                            )}
                                            style={{ height: `${(datum.values[item.key] / total) * 100}%`, background: item.color }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )
                    })}
                </div>

                {activeDatum && active !== null && (
                    <ChartTooltip
                        title={activeDatum.tooltipLabel}
                        x={center(active)}
                        footer={(
                            <ChartTooltipRow label={totalLabel} value={formatValue(totals[active])} />
                        )}
                    >
                        {[...series].reverse().map((item) => (
                            <ChartTooltipRow
                                key={item.key}
                                swatch={<Swatch color={item.color} />}
                                label={item.label}
                                value={formatValue(activeDatum.values[item.key] ?? 0)}
                            />
                        ))}
                    </ChartTooltip>
                )}
            </ChartFrame>

            <p className='sr-only' aria-live='polite'>
                {activeDatum && active !== null ? `${activeDatum.tooltipLabel}: ${totalLabel} ${formatValue(totals[active])}` : ''}
            </p>
        </div>
    )
}
